import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Scissors, ArrowRight, ArrowLeft } from "lucide-react";
import PlatformLogo from "@/components/PlatformLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [verification, setVerification] = useState(null); // { email, resent? }
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setVerification(null);
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role === "super_admin") navigate("/admin");
      else navigate("/cms");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns { code: "verification_required", email, message } for unverified salons.
      if (detail && typeof detail === "object" && detail.code === "verification_required") {
        setVerification({ email: detail.email || email, message: detail.message });
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const resendVerify = async () => {
    if (!verification?.email) return;
    try {
      await api.post("/public/register/resend", { email: verification.email });
      setVerification((v) => ({ ...v, resent: "ok" }));
    } catch (err) {
      setVerification((v) => ({ ...v, resent: "err", resendErr: formatApiError(err.response?.data?.detail) }));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden" style={{ background: "var(--mv-surface)" }}>

        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(44,38,33,0.15) 0%, rgba(44,38,33,0.55) 100%)" }} />
        <div className="relative z-10 max-w-md text-white p-12">
          <Link to="/" data-testid="login-home-link-desktop" className="flex items-center gap-2 mb-8 opacity-90 hover:opacity-100 transition-opacity">
            <PlatformLogo imgClassName="h-7 w-auto object-contain">
              <Scissors className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-display text-lg tracking-wide">StudioMV</span>
            </PlatformLogo>
          </Link>
          <h1 className="font-serif-mv text-5xl leading-tight mb-4">Ihre Salon-Identität, online entfaltet.</h1>
          <p className="text-base text-white/80 leading-relaxed">Salonsoftware & Website-CMS in einem. Bauen, gestalten, veröffentlichen – ohne Code.</p>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-8 lg:p-16">
        <Link
          to="/"
          data-testid="login-back-to-home"
          className="absolute top-6 left-6 lg:top-8 lg:left-8 inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity"
          style={{ color: "var(--mv-text-muted)" }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Zur Startseite
        </Link>
        <form onSubmit={onSubmit} className="w-full max-w-sm mv-fade-in" data-testid="login-form">
          <Link to="/" data-testid="login-home-link-mobile" className="lg:hidden flex items-center gap-2 mb-8">
            <PlatformLogo imgClassName="h-7 w-auto object-contain">
              <Scissors className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <span className="font-display text-lg">StudioMV</span>
            </PlatformLogo>
          </Link>
          <p className="mv-label mb-3">Willkommen zurück</p>
          <h2 className="font-display text-3xl mb-2" style={{ color: "var(--mv-text)" }}>Im Studio anmelden</h2>
          <p className="text-sm mb-8" style={{ color: "var(--mv-text-muted)" }}>Verwalten Sie Ihren Salon und Ihre Website.</p>

          <label className="mv-label block mb-2">E-Mail</label>
          <input
            data-testid="login-email-input"
            type="email"
            className="mv-input mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />

          <label className="mv-label block mb-2">Passwort</label>
          <input
            data-testid="login-password-input"
            type="password"
            className="mv-input mb-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && (
            <p data-testid="login-error" className="text-sm mt-3 mb-2" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
          )}

          {verification && (
            <div className="mt-4 rounded-xl border p-4 text-sm" style={{ borderColor: "var(--mv-primary)", background: "rgba(200,122,95,0.08)" }} data-testid="login-verification-required">
              <p className="font-medium mb-1">E-Mail noch nicht bestätigt</p>
              <p className="mb-3" style={{ color: "var(--mv-text-muted)" }}>
                {verification.message || `Wir haben einen Bestätigungs-Link an ${verification.email} geschickt. Bitte klicke darauf, um dein Konto zu aktivieren.`}
              </p>
              <button type="button" onClick={resendVerify} className="text-xs underline" data-testid="login-resend-verify-btn">
                Bestätigungs-Mail erneut senden
              </button>
              {verification.resent === "ok" && (
                <p className="text-xs mt-2 text-green-700" data-testid="login-resend-ok">Neuer Link gesendet. Bitte E-Mail-Postfach prüfen.</p>
              )}
              {verification.resent === "err" && (
                <p className="text-xs mt-2 text-red-600">{verification.resendErr || "Senden fehlgeschlagen — gleich nochmal versuchen."}</p>
              )}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy}
            className="mv-btn w-full mt-6"
          >
            {busy ? "Anmelden..." : "Anmelden"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>

          <p className="text-sm text-center mt-4">
            <Link to="/forgot-password" data-testid="login-forgot-password-link" className="opacity-75 hover:opacity-100 underline">
              Passwort vergessen?
            </Link>
          </p>

          <p className="text-sm text-center mt-5" style={{ color: "var(--mv-text-muted)" }}>
            Noch kein Konto?{" "}
            <Link to="/pricing" data-testid="login-register-link" className="underline" style={{ color: "var(--mv-primary)" }}>
              Jetzt Salon registrieren
            </Link>
          </p>

          
        </form>
      </div>
    </div>
  );
}
